import { formatPropertyAddress } from '../utils/propertyAddress';
import { propertyFetch } from '../services/idxPropertyClient';
import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { SITE_CONFIG } from '../config';
import { SEO_CONFIG } from '../config/seo';
import { useTheme } from '../contexts/ThemeContext';
import { useTranslation } from 'react-i18next';
import './IDXTownPage.css';
import SEOLinks from './SEOLinks';

const WestminsterFirstTimeHomebuyersPage = () => {
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const { t } = useTranslation();
  const [properties, setProperties] = useState([]);
  const [propertyImages, setPropertyImages] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.body.classList.remove('intro-active');
    document.body.style.overflow = 'auto';
    document.body.style.position = 'static';
    if (isDarkMode) {
      document.body.classList.add('dark-mode');
    } else {
      document.body.classList.remove('dark-mode');
    }
    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
    };
  }, [isDarkMode]);

  useEffect(() => {
    const fetchProperties = async () => {
      try {
        const response = await propertyFetch(`https://tophamtechbe.com/api/treas/idx/v1/retrieve-property-summary?city=Westminster&maxPrice=800000&county=Orange&state=CA&firstPhoto=true&hasMedia=true&limit=9`, {});
        if (response.ok) {
          const data = await response.json();
          const props = data.properties || [];
          setProperties(props);
          props.forEach(p => {
            if (p.listing_key) {
              propertyFetch(`https://tophamtechbe.com/api/treas/idx/v1/retrieve-property-media/${p.listing_key}`, {}).then(r => r.json()).then(d => {
                const first = (d.media || []).sort((a, b) => parseInt(a.order_hint) - parseInt(b.order_hint))[0];
                const url = first?.media_url || first?.s3_link;
                if (url) setPropertyImages(prev => ({ ...prev, [p.listing_key]: url }));
              }).catch(() => {});
            }
          });
        }
      } catch (error) {
        console.error('Error fetching properties:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProperties();
  }, []);

  return (
    <div className="idxtown-page">
      <Helmet>
        <title>Westminster CA First-Time Homebuyers | Expert Guidance | Truc Doan</title>
        <meta name="description" content="First-time homebuyer in Westminster, CA? Truc Doan provides expert guidance on FHA loans, down payment assistance, and affordable starter homes in Westminster and Little Saigon. Free consultation." />
        <meta name="keywords" content="first-time homebuyer Westminster CA, FHA loans Westminster, down payment assistance Orange County, starter homes Westminster, affordable homes Westminster CA, first time buyer programs California, Truc Doan first-time buyer" />
        <link rel="canonical" href={`${SEO_CONFIG.DOMAIN}/westminster-first-time-homebuyers`} />
        <meta property="og:title" content="Westminster CA First-Time Homebuyers | Expert Guidance | Truc Doan" />
        <meta property="og:description" content="First-time homebuyer? Get expert guidance on FHA loans, down payment assistance, and affordable starter homes in Westminster, CA." />
        <meta property="og:url" content={`${SEO_CONFIG.DOMAIN}/westminster-first-time-homebuyers`} />
        <meta property="og:type" content="website" />
        <meta property="og:image" content={SEO_CONFIG.OG_IMAGE} />
        <meta name="geo.region" content="US-CA" />
        <meta name="geo.placename" content="Westminster, California" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "RealEstateAgent",
            "name": "Truc Doan",
            "telephone": "+1-818-818-0989",
            "email": "doanestate@gmail.com",
            "url": `${SEO_CONFIG.DOMAIN}/westminster-first-time-homebuyers`,
            "image": SEO_CONFIG.OG_IMAGE,
            "description": "Truc Doan helps first-time homebuyers in Westminster, CA navigate FHA loans, down payment assistance programs, and find affordable starter homes.",
            "areaServed": {
              "@type": "City",
              "name": "Westminster, California"
            },
            "address": {
              "@type": "PostalAddress",
              "addressLocality": "Westminster",
              "addressRegion": "CA",
              "postalCode": "92683",
              "addressCountry": "US"
            }
          })}
        </script>
      </Helmet>

      <div className="idxtown-container">
        <button
          onClick={() => navigate('/')}
          style={{ position: 'absolute', top: '20px', left: '20px', padding: '10px 20px', background: 'rgb(14, 165, 233)', color: 'rgb(243, 244, 246)', border: 'none', borderRadius: '5px', cursor: 'pointer', fontSize: '16px', fontWeight: 'bold', zIndex: 10 }}
        >
          {t('nav.home')}
        </button>

        <header className="idxtown-header">
          <h1>Westminster First-Time Homebuyers Guide</h1>
          <p style={{ marginTop: '16px', fontSize: '18px', opacity: 0.9 }}>
            Expert guidance to help you buy your first home in Westminster, CA — from pre-approval to closing day.
          </p>
        </header>

        <section className="idxtown-vibe">
          <h2>Your First Home in Westminster</h2>
          <p>Buying your first home is one of life's biggest milestones, and Westminster offers excellent opportunities for first-time buyers. With a range of affordable condos, townhomes, and single-family starter homes, Westminster provides a path to homeownership that many other Orange County cities cannot match.</p>
          <p>Truc Doan specializes in guiding first-time buyers through every step of the process — from understanding your budget and getting pre-approved, to finding the right home and navigating escrow. You don't have to figure it out alone.</p>
        </section>

        <section className="idxtown-lifestyle">
          <h2>First-Time Buyer Programs & Resources</h2>
          <p>Take advantage of programs designed to make homeownership accessible:</p>
          <div className="amenities-list">
            <div className="amenity">
              <h3>FHA Loans</h3>
              <p>Put as little as 3.5% down with an FHA loan. Lower credit score requirements make this ideal for first-time buyers building their financial history.</p>
            </div>
            <div className="amenity">
              <h3>Down Payment Assistance</h3>
              <p>California offers programs like CalHFA and MyHome that provide down payment and closing cost assistance for qualified first-time buyers.</p>
            </div>
            <div className="amenity">
              <h3>Conventional 3% Down</h3>
              <p>Many lenders offer conventional loans with just 3% down for first-time buyers with good credit scores.</p>
            </div>
            <div className="amenity">
              <h3>Affordable Neighborhoods</h3>
              <p>Westminster's west side and areas near Bolsa Chica offer starter homes and condos in the $450K–$650K range.</p>
            </div>
            <div className="amenity">
              <h3>Starter Condos & Townhomes</h3>
              <p>Build equity with a condo or townhome before upgrading. Westminster has many well-maintained communities with low HOA fees.</p>
            </div>
            <div className="amenity">
              <h3>Free Buyer Consultation</h3>
              <p>Truc Doan offers a free no-obligation consultation to assess your readiness, explain the process, and create a personalized buying plan.</p>
            </div>
          </div>
        </section>

        <section className="market-snapshot">
          <h2>Westminster Starter Home Market Snapshot</h2>
          <p>Westminster remains one of the most affordable entry points into Orange County homeownership.</p>
          <table className="market-stats">
            <thead>
              <tr>
                <th>Market Stat</th>
                <th>Value</th>
              </tr>
            </thead>
            <tbody>
              <tr><td>Median Starter Home Price</td><td>~$600,000</td></tr>
              <tr><td>Avg Days on Market</td><td>15–25 Days</td></tr>
              <tr><td>Most Active Price Range</td><td>$450K – $700K</td></tr>
              <tr><td>Popular Starter Types</td><td>Condos, Townhomes, Small SFR</td></tr>
              <tr><td>FHA-Eligible Properties</td><td>Many — condos & single family</td></tr>
            </tbody>
          </table>
        </section>

        <section className="properties-section">
          <h2>Affordable Westminster Homes for First-Time Buyers</h2>
          {loading ? (
            <p style={{ color: 'rgb(156, 163, 175)' }}>Loading properties...</p>
          ) : properties.length > 0 ? (
            <div className="properties-grid">
              {properties.slice(0, 9).map((property) => (
                <div
                  key={property.listing_key}
                  className="property-card"
                  onClick={() => navigate(`/listing/${property.listing_key}`)}
                  style={{
                    backgroundImage: (propertyImages[property.listing_key] || property.first_photo_url) ? `url(${propertyImages[property.listing_key] || property.first_photo_url})` : 'none',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center'
                  }}
                >
                  <div className="property-price">${property.current_price?.toLocaleString()}</div>
                  <div className="property-address">{formatPropertyAddress(property)}, CA {property.postal_code}</div>
                  <div className="property-type">{property.property_sub_type}</div>
                  {(property.list_agent_full_name || property.list_office_name) && (
                    <div style={{fontSize: '11px', color: '#ddd', marginTop: '2px'}}>Listed by: {[property.list_agent_full_name, property.list_office_name].filter(Boolean).join(' | ')}</div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p style={{ color: 'rgb(156, 163, 175)' }}>Browse all affordable Westminster listings by clicking "Search Listings" below.</p>
          )}
        </section>

        <section className="cta-section">
          <h2>Ready to Buy Your First Home in Westminster?</h2>
          <p>Contact Truc Doan for a free first-time buyer consultation. No pressure, just expert guidance.</p>
          <div className="cta-buttons">
            <a href="mailto:doanestate@gmail.com?subject=Westminster First-Time Homebuyer Inquiry" className="cta-button primary">Email Truc Doan</a>
            <a href="tel:+1-818-818-0989" className="cta-button secondary">Call +1-818-818-0989</a>
            <button className="cta-button primary" onClick={() => navigate('/chat')} style={{ border: 'none', cursor: 'pointer' }}>Search Listings</button>
          </div>
        </section>
      </div>

      <div style={{ background: 'rgba(0,0,0,0.85)', padding: '6px 12px', display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
        <img src="https://aw-client-logos-and-headshots.s3.us-east-2.amazonaws.com/logos/crmlslogo.png" alt="CRMLS" style={{ height: '20px', objectFit: 'contain' }} />
        <img src="https://aw-client-logos-and-headshots.s3.us-east-2.amazonaws.com/logos/equalhousinglogo.png" alt="Equal Housing Opportunity" style={{ height: '20px', objectFit: 'contain' }} />
        <span style={{ fontSize: '10px', color: '#ccc', flex: 1 }}>Based on information from California Regional Multiple Listing Service, Inc. and/or other sources. All data, including all measurements and calculations of area, is obtained from various sources and has not been, and will not be, verified by broker or MLS. All information should be independently reviewed and verified for accuracy. Properties may or may not be listed by the office/agent presenting the information.</span>
      </div>
      <SEOLinks />
    </div>
  );
};

export default WestminsterFirstTimeHomebuyersPage;
